import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import './ConfirmDialog.css';

// Reusable modal replacement for window.confirm(). Any page can render one
// of these instead of calling window.confirm() directly — pass `open` plus
// the copy and handlers, and it renders nothing while `open` is false.
// `items` is an optional bullet list of consequences shown under `message`.
const ConfirmDialog = ({
    open,
    icon: Icon,
    title = 'Are you sure?',
    message,
    items,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    danger = false,
    onConfirm,
    onCancel,
}) => {
    const confirmRef = useRef(null);
    const DialogIcon = Icon || (danger ? Trash2 : AlertTriangle);

    useEffect(() => {
        if (!open) return undefined;
        confirmRef.current?.focus();
        const closeOnEscape = event => {
            if (event.key === 'Escape') onCancel?.();
        };
        document.addEventListener('keydown', closeOnEscape);
        return () => document.removeEventListener('keydown', closeOnEscape);
    }, [open, onCancel]);

    if (!open) return null;

    return (
        <div className="confirm-dialog-overlay" role="presentation" onClick={() => onCancel?.()}>
            <div
                className="confirm-dialog"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="confirm-dialog-title"
                aria-describedby={message ? 'confirm-dialog-message' : undefined}
                onClick={event => event.stopPropagation()}
            >
                <div className="confirm-dialog-icon-wrap">
                    <span className={`confirm-dialog-icon ${danger ? 'danger' : ''}`}>
                        <DialogIcon size={22} />
                    </span>
                </div>

                <h3 id="confirm-dialog-title">{title}</h3>
                {message && <p id="confirm-dialog-message">{message}</p>}
                {items?.length > 0 && (
                    <ul className="confirm-dialog-items">
                        {items.map(item => <li key={item}>{item}</li>)}
                    </ul>
                )}

                <div className="confirm-dialog-actions">
                    <button type="button" className="confirm-dialog-btn cancel" onClick={onCancel}>
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        ref={confirmRef}
                        className={`confirm-dialog-btn confirm ${danger ? 'danger' : ''}`}
                        onClick={onConfirm}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmDialog;
